export interface LocationGuide {
  slug: string;
  title: string;
  description: string;
  seo_title: string | null;
  seo_description: string | null;
  seo_keywords: string | null;
  content: string;
  planner_lat: number | null;
  planner_lon: number | null;
  planner_name: string | null;
}

const modules = import.meta.glob('/content/location-guides/*.md', {
  eager: true,
  query: '?raw',
  import: 'default',
}) as Record<string, string>;

/** Parse YAML frontmatter without gray-matter (which needs Node Buffer) */
function parseFrontmatter(raw: string): { data: Record<string, string>; content: string } {
  const match = raw.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n([\s\S]*)$/);
  if (!match) return { data: {}, content: raw };

  const yamlBlock = match[1];
  const content = match[2];
  const data: Record<string, string> = {};

  for (const line of yamlBlock.split('\n')) {
    const idx = line.indexOf(':');
    if (idx === -1) continue;
    const key = line.slice(0, idx).trim();
    let value = line.slice(idx + 1).trim();
    // Strip surrounding quotes
    if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
      value = value.slice(1, -1);
    }
    data[key] = value;
  }

  return { data, content };
}

function parseGuide(raw: string, filepath: string): LocationGuide {
  const { data, content } = parseFrontmatter(raw);

  const slug =
    data.slug ||
    filepath
      .split('/')
      .pop()!
      .replace(/\.md$/, '');

  return {
    slug,
    title: data.title || slug,
    description: data.description || '',
    seo_title: data.seo_title || null,
    seo_description: data.seo_description || null,
    seo_keywords: data.seo_keywords || null,
    content,
    planner_lat: data.planner_lat ? parseFloat(data.planner_lat) : null,
    planner_lon: data.planner_lon ? parseFloat(data.planner_lon) : null,
    planner_name: data.planner_name || null,
  };
}

let _guides: LocationGuide[] | null = null;

function loadGuides(): LocationGuide[] {
  if (_guides) return _guides;
  _guides = Object.entries(modules).map(([filepath, raw]) => parseGuide(raw, filepath));
  return _guides;
}

export function getAllGuides(): LocationGuide[] {
  return loadGuides();
}

export function getGuideBySlug(slug: string): LocationGuide | undefined {
  return loadGuides().find((g) => g.slug === slug);
}

export function getAllGuideSlugs(): string[] {
  return loadGuides().map((g) => g.slug);
}
